import { useEffect, useState } from "react";
import { Radar, Trash2, Plus, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const RADII = [
  { v: "50", l: "50 km" },
  { v: "100", l: "100 km" },
  { v: "national", l: "Național (toată țara)" },
];

const radiusLabel = (km) => (km == null ? "Național" : `${km} km`);

export const SupplierAlerts = () => {
  const { user } = useAuth();
  const [counties, setCounties] = useState([]);
  const [tree, setTree] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({ county: "", radius: "100", mk_primary: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!user?.email) return;
    api.get("/supplier-alerts", { params: { email: user.email } }).then((r) => setAlerts(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/ro-locations").then((r) => setCounties(r.data.counties)).catch(() => {});
    api.get("/marketplace-taxonomy").then((r) => setTree(r.data.tree)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const save = async () => {
    if (!form.county) { toast.error("Alege un județ de referință."); return; }
    setSaving(true);
    try {
      await api.post("/supplier-alerts", {
        email: user.email,
        county: form.county,
        radius_km: form.radius === "national" ? null : parseInt(form.radius, 10),
        mk_primary: form.mk_primary,
      });
      toast.success("Alertă de proximitate activată.");
      setForm({ county: "", radius: "100", mk_primary: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/supplier-alerts/${id}`);
    toast.success("Alertă dezactivată.");
    load();
  };

  const catName = (id) => (tree.find((g) => g.id === id) || {}).name || "Toate categoriile";

  return (
    <section className="mt-14" data-testid="supplier-alerts">
      <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2 flex items-center gap-2">
        <Radar className="w-4 h-4" /> Alerte de proximitate — cereri în zona ta
      </h2>
      <p className="text-sm text-slate-500 mb-5 max-w-2xl">
        Primești un email când apare o <b>cerere nouă</b> în raza aleasă față de județul tău — ideal pentru furnizori care vor lead-uri logistice apropiate.
      </p>

      <div className="border border-slate-200 p-5 grid gap-4 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end bg-slate-50" data-testid="supplier-alert-form">
        <div>
          <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Județ de referință</label>
          <Select value={form.county} onValueChange={(v) => setForm((f) => ({ ...f, county: v }))}>
            <SelectTrigger className="rounded-none mt-1.5 bg-white" data-testid="supplier-county"><SelectValue placeholder="Alege județul" /></SelectTrigger>
            <SelectContent className="max-h-[50vh]">
              {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Rază de notificare</label>
          <Select value={form.radius} onValueChange={(v) => setForm((f) => ({ ...f, radius: v }))}>
            <SelectTrigger className="rounded-none mt-1.5 bg-white" data-testid="supplier-radius"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RADII.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide font-bold text-slate-500">Categorie (opțional)</label>
          <Select value={form.mk_primary || "__all"} onValueChange={(v) => setForm((f) => ({ ...f, mk_primary: v === "__all" ? "" : v }))}>
            <SelectTrigger className="rounded-none mt-1.5 bg-white" data-testid="supplier-category"><SelectValue placeholder="Toate categoriile" /></SelectTrigger>
            <SelectContent className="max-h-[50vh]">
              <SelectItem value="__all">Toate categoriile</SelectItem>
              {tree.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button onClick={save} disabled={saving}
          className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50 h-[42px]"
          data-testid="supplier-alert-save">
          <Plus className="w-4 h-4" /> Activează
        </button>
      </div>

      {alerts.length > 0 && (
        <div className="mt-5 border border-slate-200 divide-y divide-slate-100" data-testid="supplier-alert-list">
          {alerts.map((a) => (
            <div key={a.id} className="flex items-center gap-4 p-4" data-testid={`supplier-alert-${a.id}`}>
              <div className="w-10 h-10 bg-orange-50 text-orange-600 flex items-center justify-center shrink-0"><Radar className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {a.county} · rază {radiusLabel(a.radius_km)}</div>
                <div className="text-xs text-slate-500">{a.mk_primary ? catName(a.mk_primary) : "Toate categoriile"}</div>
              </div>
              <div className="text-right shrink-0" data-testid={`supplier-alert-stat-${a.id}`}>
                <div className={`text-2xl font-black tracking-tight ${a.recent_matches_7d > 0 ? "text-orange-600" : "text-slate-300"}`}>{a.recent_matches_7d ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 leading-tight">cereri noi<br />în 7 zile</div>
              </div>
              <button onClick={() => remove(a.id)} className="p-2 hover:bg-red-50 text-red-600" data-testid={`supplier-alert-delete-${a.id}`} title="Dezactivează">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
